<template>
  <div>
    <div class="form-container ou-donation-form">
      <div class="holder">
        <PaymentForm class="form-horizontal">
          <div class="form-section form-section--flex">
            <div class="main-donation">
              <FormSection title="Personal Info">
                <FormRow inline>
                  <FormCol field="title" :flex="1" />
                    <FormCol field="firstName" :flex="2" />
                    <FormCol field="lastName" :flex="2" />
                  </FormRow>
                  <FormRow inline>
                    <FormCol field="email" />
                    <FormCol field="phone" />
                  </FormRow>
                  <FormRow inline>
                    <Consent privacy="https://www.yachad.org/privacy-policy/"/>
                  </FormRow>
                  <FormRow>
                  </FormRow>
                 <FormRow inline>
                    <FormCol field="custom.extras" :showLabel="false" width="full">
                      <label class="control-label">Event Type</label>
                      <div style="display: flex; gap: 10px; align-items: flex-start;">
                        <FormSelect>
                          <option value="">Select Event Type</option>
                          <option value="Leadership Shabbaton">Leadership Shabbaton</option>
                          <option value="Other">Other</option>
                        </FormSelect>
                      </div>
                      <ErrorLabel field="custom.extras" />
                    </FormCol>
                  </FormRow>


                  <FormRow inline v-if="getValue('custom.extras') === 'Other'">
                    <FormCol field="custom.designation" :showLabel="false" width="full">
                      <label class="control-label">Region</label>
                      <FormSelect>
                        <option value="">Select Region</option>
                        <option value="National">Yachad National</option>
                        <option value="New York">Yachad New York</option>
                        <option value="New Jersey">Yachad New Jersey</option>
                        <option value="Chicago">Yachad Chicago</option>
                        <option value="New England">Yachad New England</option>
                        <option value="Baltimore">Yachad Baltimore</option>
                        <option value="Cleveland">Yachad Cleveland</option>
                        <option value="Dallas">Yachad Dallas</option>
                        <option value="Detroit">Yachad Detroit</option>
                        <option value="Houston">Yachad Houston</option>
                        <option value="Los Angeles">Yachad Los Angeles</option>
                        <option value="Omaha">Yachad Omaha</option>
                        <option value="South Florida">Yachad South Florida</option>
                        <option value="St. Louis">Yachad St. Louis</option>
                        <option value="Toronto">Yachad Toronto</option>
                        <option value="Philadelphia">Yachad Philadelphia</option>
                        <option value="Israel">Yachad Israel</option>
                      </FormSelect>
                      <ErrorLabel field="custom.designation" />
                    </FormCol>
                    <!-- New York Chapter Selection -->
                    <FormCol v-if="getValue('custom.designation') === 'New York'" field="custom.chapter" :showLabel="false" width="full">
                      <label class="control-label">Chapter</label>
                      <FormSelect>
                        <option :value="null">Select Chapter</option>
                        <option
                          v-for="(nyChapter, index) in ['General', 'Long Island', 'Brooklyn', 'Monsey/Manhattan']"
                          :key="index"
                          :value="nyChapter"
                          >
                          {{ nyChapter }}
                        </option>
                      </FormSelect>
                      <ErrorLabel field="custom.chapter" label="Please Select a Chapter" />
                    </FormCol>
                  </FormRow>
                </FormSection>
                <FormSection title="Billing Information" >
                  <DonorAddress />
                </FormSection>
                <FormSection title="Payment Amount">
                  <div class="donateion__list">
                    <AmountSelectorSimple/>
                    <ErrorLabel field="amount" />
                  </div>
                  <br>
                  <FormRow inline style="max-width: 50%;">
                    <FormCol field="custom.Notes" :showLabel="false" width="full">
                      <label class="control-label">Payment Description</label>
                      <FormInput type="text" class="width-full" field="custom.Notes" />
                    </FormCol>
                    <ErrorLabel field="custom.Notes" />
                  </FormRow>
                  <!-- <ShortNote/> -->
                </FormSection>
                <FormSection title="Payment Frequency">
                  <div class="frequency">
                    <PaymentOptions addSplit />
                  </div>
                </FormSection>
              <FormSection title="Payment Information" last>
                <FormRow inline>
                  <FormCol field="card" :showLabel="false"/>
                </FormRow>
                <PaymentButton showAmount btnText="Submit"/>
              </FormSection>
            </div>
          </div>
        </PaymentForm>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch} from "vue";
import { usePaymentForm } from '~/shared/use-payment-form'

export default defineComponent({
  setup() {
    const { setValue, getValue, form, submit } = usePaymentForm()

    // Initialize gateway & base description
    setValue('gateway','SensepassCardknoxOUSpec')
    // We'll maintain description reactively; start with default
    setValue('description','Yachad Internal Payment')
    setValue('custom.designation', 'National')

    // Helper to compose description according to spec
    const composeDescription = () => {
    const notes = getValue('custom.Notes') as string | null
    const region = getValue('custom.designation') as string | null
    const nyChapter = getValue('custom.chapter') as string | null

    const note = notes && notes.trim().length > 0 ? notes.trim() : ''

    if (!region) {
      return note || 'Yachad Internal Payment'
    }

    // Region (+ NY chapter) first, then the Payment Description
    const chapterPart = region === 'New York' && nyChapter ? ` | ${nyChapter}` : ''
    const prefix = `Yachad ${region}${chapterPart}`
    return note ? `${prefix} - ${note}` : prefix
  }

    // Region -> Department codes mapping
    const regionDepartmentMap: Record<string, string> = {
      'New York': 'Yachad_NewYork',
      'National': 'Yachad',
      'New Jersey': 'Yachad_NewJersey',
      'Chicago': 'Yachad_Chicago',
      'New England': 'Yachad_NewEngland',
      'Baltimore': 'Yachad_Baltimore',
      'Cleveland': 'Yachad_Cleveland',
      'Dallas': 'Yachad_Dallas',
      'Detroit': 'Yachad_Detroit',
      'Houston': 'Yachad_Houston',
      'Los Angeles': 'Yachad_LosAngeles',
      'Omaha': 'Yachad_Omaha',
      'South Florida': 'Yachad_Florida',
      'St. Louis': 'Yachad_StLouis',
      'Toronto': 'Yachad_Toronto',
      'Philadelphia': 'Yachad_Philadelphia',
      'Israel': 'Yachad_Israel'
    }

    // Watch dependent fields; avoid infinite loop by only updating if value changed
    let descTick: any
    watch(
      () => [
        getValue('custom.Notes'),
        getValue('custom.designation'),
        getValue('custom.chapter')
      ],
      () => {
        const next = composeDescription()
        const current = getValue('description') as string | null
        if (current === next) return
        clearTimeout(descTick)
        // tiny debounce to avoid multiple rapid updates
        descTick = setTimeout(() => {
          setValue('description', next)
        }, 0)
      },
      { immediate: true }
    )



    return {
      form,
      setValue,
      submit,
      getValue,
    }
  },
})
</script>
<style scoped lang="scss">
h2.payment-heading {
  display: flex;
  justify-content: space-between;
}
.card-img {
  display: flex;
  img {
    height: 23px;
  }
}
.fee-total {
  margin-bottom: 0;
  justify-content: space-between;
}
.form-container.ou-donation-form {
  margin: 0px auto;
  padding: 0px 10px;
  box-shadow: none;
  max-width: 100%;
  @media only screen and (min-width: 940px) {

  }
}
::v-deep .ou-donation-form {
  label {
    margin-bottom: 0px !important;
    text-transform: none;
    color: #000;
    font-weight: 400;
    font-size: 14px;
  }
  label.radio-inline {
    display: flex;
    gap: 5px;
    padding-bottom: 4px;
  }
  .styled-radio-container.styled-radio-container--ou-donation.row {
    .radio-part {
      @media only screen and (min-width: 720px) {
        min-width:200px;
        max-width: 200px;
      }
    }
  }
  hr {
    margin-bottom: 25px;
    border-left: 0px;
    padding-bottom: 10px;
    border-top: none;
    margin-top: 0px;
    border-bottom: 1px solid #efefef;
  }
  button.ou-donation-btn.btn-flex {
    margin: 0px;
    background: #ee6f1f;
    width: 250px;
    max-width: 100%;
    text-transform: uppercase;
    text-align: center;
    color: white;
    border:none;
    padding: 15px 0px;
    font-weight: 600;
    letter-spacing: 1px;
    -webkit-box-shadow: 0 0 10px #ddd;
    box-shadow: 0 0 10px #ddd;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
  }
}
::v-deep h2 {
  color: #214ba7;
  text-transform: capitalize;
  margin-top:0px;
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 700;
  @media only screen and (min-width: 720px) {
    font-size: 18px;
  }
}
.form-section {
  margin-bottom: 30px;
  padding: 30px 0px;
  margin: 0px auto 25px;

  @media only screen and (min-width: 720px) {
    padding: 0px 0px 30px;
    max-width: 1200px;
    background: white;
  }
  .form-section__inner {
    max-width: 700px;
  }
}

::v-deep .oudonate-other-amount .amount_other__wrapper:before{
  height: auto;
}
::v-deep .styled-radio-container--ou-donation.styled-radio-container {
  .amount-row {
    min-width: 100%;
    max-width: 100%;
    @media only screen and (min-width: 720px) {
      min-width: 100%;
      max-width: 100%;

      &.amount_other__wrapper {
        min-width: 49%;
        max-width: 49%;
      }
    }
    .label-description {
      font-size: 14px;
      text-transform: none;
    }
  }
  input[type="radio"] + label {
    color: black;
  }
  input[type="radio"]:checked + label .label-amount {
    background: #15329d;
    border-right-color: gray !important;
    border-color: #15329d;
  }
  input[type="radio"]:checked + label {
    background: #15329d;
    color: white;
  }
}
::v-deep .inhonor{
  .form-group{
    display: flex;
  }
}
::v-deep .donateion__list {
  .label-amount{
    min-width: 90px;
    max-width: 90px;
  }
}
.control-label {
  text-transform: none;
  color: #000;
  font-weight: 400;
  font-size: 14px;
  padding-top: 3px;
  padding-bottom: 3px;
  display: block;
}

</style>

